// ============================================================
// Site statistics — every truck that arrived in the demo history
// (statsData.js: planned − no-shows) gets a gate, an arrival hour
// and its times on site. Silo trucks load at CLS, pallets at PST.
// Arrivals bunch into the 6–9 and 12–14 peaks; a truck waits
// when all loading points of its gate are busy.
// ============================================================

import { history, rng } from './statsData';

export const GATES = ['CLS', 'PST'];
export const HOURS = Array.from({ length: 16 }, (_, i) => i + 5); // 05–20
// Loading points per gate (illustrative, to be confirmed by the plant).
const LOADING_POINTS = { CLS: 2, PST: 1 };
const LOAD_MINUTES = { silo: [22, 38], pallet: [35, 60] };
const SILO_SHARE = 0.68;
const CHECK_IN_MINUTES = 5; // gate, weighing in
const EXIT_MINUTES = 12; // weighing out and paperwork
// Trucks a gate handles per hour: loading points × 60 / average loading time.
export const GATE_CAPACITY = {
  CLS: Math.round((LOADING_POINTS.CLS * 60) / 30),
  PST: Math.round((LOADING_POINTS.PST * 60) / 47.5),
};

// Relative arrival weight per hour (05–20).
const HOUR_WEIGHT = [1, 5, 8, 8, 4, 1.5, 1.5, 2, 7, 8, 3, 1.5, 1, 0.5, 0.3, 0.2];
function pickHour(r, weights) {
  const sum = weights.reduce((s, w) => s + w, 0);
  let x = r() * sum;
  for (let i = 0; i < weights.length; i += 1) {
    x -= weights[i];
    if (x <= 0) return HOURS[i];
  }
  return HOURS[HOURS.length - 1];
}

function buildVisits() {
  const r = rng(2911);
  return history.map(day => {
    const visits = Array.from({ length: day.planned - day.noShows }, () => {
      const type = r() < SILO_SHARE ? 'silo' : 'pallet';
      const hour = pickHour(r, HOUR_WEIGHT);
      const [min, max] = LOAD_MINUTES[type];
      return {
        type,
        gate: type === 'silo' ? 'CLS' : 'PST',
        hour,
        arrival: hour * 60 + Math.floor(r() * 60),
        loadMinutes: Math.round(min + r() * (max - min)),
      };
    });
    // Queue per gate: a truck waits until one of the gate's loading points is free.
    GATES.forEach(gate => {
      const freeAt = Array(LOADING_POINTS[gate]).fill(0);
      visits.filter(v => v.gate === gate).sort((a, b) => a.arrival - b.arrival).forEach(v => {
        const point = freeAt.indexOf(Math.min(...freeAt));
        const start = Math.max(v.arrival + CHECK_IN_MINUTES, freeAt[point]);
        freeAt[point] = start + v.loadMinutes;
        v.waitMinutes = start - v.arrival;
        v.dwellMinutes = v.waitMinutes + v.loadMinutes + EXIT_MINUTES;
      });
    });
    return { date: day.date, weekday: new Date(day.date).getDay(), visits };
  });
}

export const siteHistory = buildVisits();

// Average arrivals per weekday (Mon–Fri) and hour at one gate in the last `days` days.
export function arrivalHeatmap(days, gate) {
  const rows = siteHistory.slice(-days);
  return [1, 2, 3, 4, 5].map(weekday => {
    const sameDay = rows.filter(d => d.weekday === weekday);
    return {
      weekday,
      cells: HOURS.map(hour => {
        const count = sameDay.reduce((s, d) => s + d.visits.filter(v => v.gate === gate && v.hour === hour).length, 0);
        return { hour, avg: sameDay.length ? count / sameDay.length : 0 };
      }),
    };
  });
}

// Value below which `p` of the sorted numbers fall (0.5 = median).
const percentile = (xs, p) => {
  if (!xs.length) return null;
  const sorted = [...xs].sort((a, b) => a - b);
  return sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * p))];
};

// Median and 90th percentile of the times on site per loading type in the last `days` days.
export function dwellSummary(days) {
  const visits = siteHistory.slice(-days).flatMap(d => d.visits);
  const stat = (type, key) => {
    const xs = visits.filter(v => v.type === type).map(v => v[key]);
    return { median: percentile(xs, 0.5), p90: percentile(xs, 0.9) };
  };
  return ['dwellMinutes', 'waitMinutes', 'loadMinutes'].map(key => ({
    key,
    silo: stat('silo', key),
    pallet: stat('pallet', key),
  }));
}
